import { useEffect, useRef, useState } from 'react';
import type { CoverageRequirement, WizardBand, WizardLane } from '../../services/schedulerWizardApi';
import { activeWizardMembers, type WizardPrerequisites } from '../../services/schedulerWizardPrerequisites';
import { WIZARD_DAYS, wizardBand } from './schedulerWizardPresets';
import { wizardBandLabel, wizardDutyLabel } from './SchedulerPatternPreview';

export function WizardBandEditor({ band, onChange, onRemove, onCopy }: {
  band: WizardBand; onChange: (band: WizardBand) => void; onRemove: () => void;
  onCopy?: () => void;
}) {
  const change = (values: Partial<WizardBand>) => {
    const next = { ...band, ...values };
    next.endDay = WIZARD_DAYS[(WIZARD_DAYS.indexOf(next.startDay) + next.endDayOffset) % 7];
    onChange(next);
  };
  return <div className="wizard-row">
    <label>Start day<select value={band.startDay} onChange={event => change({ startDay: event.target.value })}>
      {WIZARD_DAYS.map(day => <option key={day}>{day}</option>)}</select></label>
    <label>Start time<input type="time" value={band.startTime} onChange={event => change({ startTime: event.target.value })} /></label>
    <label>End after calendar days<input type="number" min={0} max={7} value={band.endDayOffset}
      onChange={event => change({ endDayOffset: Number(event.target.value) })} /></label>
    <label>End time ({band.endDay})<input type="time" value={band.endTime}
      onChange={event => change({ endTime: event.target.value })} /></label>
    <label>Ambiguous start<select value={band.startAmbiguityResolution ?? ''}
      onChange={event => change({ startAmbiguityResolution: event.target.value === 'Earlier' ? 'Earlier' : event.target.value === 'Later' ? 'Later' : null })}>
      <option value="">Require decision if ambiguous</option><option>Earlier</option><option>Later</option></select></label>
    <label>Ambiguous end<select value={band.endAmbiguityResolution ?? ''}
      onChange={event => change({ endAmbiguityResolution: event.target.value === 'Earlier' ? 'Earlier' : event.target.value === 'Later' ? 'Later' : null })}>
      <option value="">Require decision if ambiguous</option><option>Earlier</option><option>Later</option></select></label>
    <button type="button" onClick={onRemove}>Remove band</button>
    {onCopy && <button type="button" onClick={onCopy}>Copy duty times</button>}
  </div>;
}

export function WizardPatternBandEditor({ lane, onChange }: {
  lane: WizardLane; onChange: (lane: WizardLane) => void;
}) {
  const [copySource, setCopySource] = useState<string | null>(null);
  const [targets, setTargets] = useState<string[]>([]);
  const dialog = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const source = lane.bands.find(band => band.id === copySource);
  const close = () => { setCopySource(null); trigger.current?.focus(); };
  useEffect(() => {
    if (copySource) dialog.current?.querySelector<HTMLElement>('input,button')?.focus();
  }, [copySource]);
  return <>
    {lane.bands.map(band => <WizardBandEditor key={band.id} band={band}
      onChange={next => onChange({ ...lane, bands: lane.bands.map(item => item.id === band.id ? next : item) })}
      onRemove={() => onChange({ ...lane, bands: lane.bands.filter(item => item.id !== band.id) })}
      onCopy={lane.bands.length > 1 ? () => {
        trigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setTargets([]); setCopySource(band.id);
      } : undefined} />)}
    {source && <div className="modal-overlay app-dialog-overlay" onClick={event => {
      if (event.target === event.currentTarget) close();
    }}>
      <div ref={dialog} className="modal app-dialog wizard-copy-dialog" role="dialog" aria-modal="true"
        aria-labelledby="wizard-copy-title" aria-describedby="wizard-copy-description"
        onKeyDown={event => {
          if (event.key === 'Escape') { event.stopPropagation(); close(); }
          if (event.key === 'Tab') {
            event.stopPropagation();
            const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('input,button:not(:disabled)') ?? []);
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          }
        }}>
        <h3 id="wizard-copy-title">Copy duty times from {source.startDay}</h3>
        <p id="wizard-copy-description">Copy {source.startTime} to {source.endTime} (+{source.endDayOffset} calendar days)
          to selected duty days in this band. Their start days and DST ambiguity choices stay unchanged.
          Existing times will be replaced. Other bands and service requirements are unchanged.</p>
        <button type="button" onClick={() => setTargets(lane.bands.filter(band => band.id !== source.id).map(band => band.id))}>Select all other days</button>
        {lane.bands.filter(band => band.id !== source.id).map(band => <label className="wizard-check" key={band.id}>
          <input type="checkbox" checked={targets.includes(band.id)} onChange={event =>
            setTargets(previous => event.target.checked ? [...previous, band.id] : previous.filter(id => id !== band.id))} />
          {band.startDay} ({band.startTime} to {band.endTime}, +{band.endDayOffset} days)
        </label>)}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
          <button type="button" className="btn-primary" disabled={!targets.length} onClick={() => {
            onChange({ ...lane, bands: lane.bands.map(band => targets.includes(band.id) ? {
              ...band, startTime: source.startTime, endTime: source.endTime, endDayOffset: source.endDayOffset,
              endDay: WIZARD_DAYS[(WIZARD_DAYS.indexOf(band.startDay) + source.endDayOffset) % 7],
            } : band) });
            close();
          }}>Apply duty times</button>
        </div>
      </div>
    </div>}
  </>;
}

export function WizardLaneEditor({ lane, index, snapshot, onChange, onRemove, onSelectGroup, fixed, onFixedChange }: {
  lane: WizardLane; index: number; snapshot: WizardPrerequisites | null;
  onChange: (lane: WizardLane) => void; onRemove: () => void;
  onSelectGroup: (groupId: string, fixed: boolean) => void;
  fixed: boolean; onFixedChange: (fixed: boolean) => void;
}) {
  const members = snapshot ? activeWizardMembers(snapshot, lane.groupId) : [];
  const addMember = (userId: string) => {
    if (!userId || lane.rosterUserIds.includes(userId)) return;
    onChange({ ...lane, rosterUserIds: [...lane.rosterUserIds, userId], initialUserId: lane.initialUserId || userId });
  };
  return <fieldset><legend>{wizardDutyLabel(lane, index)} ({lane.role} lane {index + 1})</legend>
    <p>{lane.bands.map(wizardBandLabel).join('; ')}</p>
    {lane.groupId && <p role="status">{members.length} active eligible members available; {lane.rosterUserIds.length} assigned to this duty.
      {!members.length && ' Select a populated group or correct membership and refresh prerequisites.'}</p>}
    <div className="wizard-row">
      <label>Staffing method<select value={fixed ? 'Fixed' : 'Rotation'} onChange={event => {
        const nextFixed = event.target.value === 'Fixed';
        onFixedChange(nextFixed);
        onChange({ ...lane, rosterUserIds: nextFixed ? lane.initialUserId ? [lane.initialUserId] : [] : lane.rosterUserIds,
          assignmentCadence: nextFixed ? 'Cycle' : lane.assignmentCadence });
      }}><option value="Rotation">Rotate eligible group members</option><option value="Fixed">One specified responder</option></select></label>
      <label>{fixed ? 'Responder source group' : 'Resource group'}<select value={lane.groupId} onChange={event =>
        onSelectGroup(event.target.value, fixed)}>
        <option value="">Select existing group</option>
        {snapshot?.groups.map(group => <option key={group.groupId} value={group.groupId}>{group.name}</option>)}
      </select></label>
      {!fixed && <label>Owner changes<select value={lane.assignmentCadence} onChange={event =>
        onChange({ ...lane, assignmentCadence: event.target.value === 'Daily' ? 'Daily' : event.target.value === 'Custom' ? 'Custom' : 'Cycle' })}>
        <option value="Cycle">Every calendar cycle (hold owner)</option>
        <option value="Daily">Daily</option><option value="Custom">Custom elapsed minutes</option>
      </select></label>}
      {!fixed && lane.assignmentCadence === 'Custom' && <label>Assignment minutes<input type="number" min={1}
        value={lane.assignmentDurationMinutes ?? 1440} onChange={event =>
          onChange({ ...lane, assignmentDurationMinutes: Number(event.target.value) })} /></label>}
      <button type="button" onClick={onRemove}>Remove lane</button>
    </div>
    {fixed ? <label>Specified responder<select value={lane.initialUserId}
      onChange={event => onChange({ ...lane, rosterUserIds: event.target.value ? [event.target.value] : [],
        initialUserId: event.target.value, assignmentCadence: 'Cycle' })}>
      <option value="">Choose one active responder</option>{members.map(member =>
        <option key={member.userId} value={member.userId}>{member.displayName}</option>)}</select></label> : <>
    <p>Group selection includes its current active eligible members. Review order, remove excluded members and choose the initial owner. Group changes never silently rewrite this roster.</p>
    <button type="button" disabled={!members.length} onClick={() => onChange({ ...lane,
      rosterUserIds: members.map(member => member.userId),
      initialUserId: members.some(member => member.userId === lane.initialUserId) ? lane.initialUserId : members[0].userId,
    })}>Replace roster with all current eligible members</button>
    <label>Add eligible responder<select value="" onChange={event => addMember(event.target.value)}>
      <option value="">Choose responder</option>
      {members.filter(member => !lane.rosterUserIds.includes(member.userId)).map(member =>
        <option key={member.userId} value={member.userId}>{member.displayName} ({member.timezone || 'timezone not set'})</option>)}
    </select></label>
    {lane.rosterUserIds.map((userId, position) => <div className="wizard-roster" key={userId}>
      <span>{position + 1}. {members.find(member => member.userId === userId)?.displayName || `Unavailable responder ${userId}`}</span>
      <button type="button" disabled={position === 0} onClick={() => {
        const roster = [...lane.rosterUserIds]; [roster[position - 1], roster[position]] = [roster[position], roster[position - 1]];
        onChange({ ...lane, rosterUserIds: roster });
      }}>Move up</button>
      <button type="button" disabled={position === lane.rosterUserIds.length - 1} onClick={() => {
        const roster = [...lane.rosterUserIds]; [roster[position + 1], roster[position]] = [roster[position], roster[position + 1]];
        onChange({ ...lane, rosterUserIds: roster });
      }}>Move down</button>
      <button type="button" onClick={() => {
        const roster = lane.rosterUserIds.filter(id => id !== userId);
        onChange({ ...lane, rosterUserIds: roster, initialUserId: lane.initialUserId === userId ? roster[0] || '' : lane.initialUserId });
      }}>Remove responder</button>
    </div>)}
    <label>Initial owner<select value={lane.initialUserId} onChange={event => onChange({ ...lane, initialUserId: event.target.value })}>
      <option value="">Choose initial owner</option>{lane.rosterUserIds.map(id =>
        <option key={id} value={id}>{members.find(member => member.userId === id)?.displayName || id}</option>)}
    </select></label></>}
    <details><summary>Edit this duty's times (advanced)</summary>
    {lane.bands.map(band => <WizardBandEditor key={band.id} band={band}
      onChange={next => onChange({ ...lane, bands: lane.bands.map(item => item.id === band.id ? next : item) })}
      onRemove={() => onChange({ ...lane, bands: lane.bands.filter(item => item.id !== band.id) })} />)}
    <button type="button" onClick={() => onChange({ ...lane, bands: [...lane.bands, wizardBand('Monday', '09:00', '17:00')] })}>Add duty band</button>
    </details>
  </fieldset>;
}

export function WizardRequirementEditor({ requirement, onChange, onRemove }: {
  requirement: CoverageRequirement; onChange: (requirement: CoverageRequirement) => void; onRemove: () => void;
}) {
  return <fieldset><legend>{requirement.role} service requirement</legend>
    <WizardBandEditor band={requirement} onChange={band => onChange({ ...requirement, ...band })} onRemove={onRemove} />
    <div className="wizard-row">
      <label>Required positions<input type="number" min={1} value={requirement.requiredPositions}
        onChange={event => onChange({ ...requirement, requiredPositions: Number(event.target.value) })} /></label>
      <label className="wizard-check"><input type="checkbox" checked={requirement.allowParallel}
        onChange={event => onChange({ ...requirement, allowParallel: event.target.checked })} />Explicit parallel staffing / handover</label>
    </div>
  </fieldset>;
}
